import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { formatApiError, patchSettings, getSettings, toastApiError } from "@/lib/api";

export default function DeskSettingsDialog({ open, onOpenChange, portfolioId, onSaved }) {
  const [form, setForm] = useState({
    auto_mode: false,
    timer_seconds: 120,
    conviction_threshold: 60,
    daily_auto_trade_cap: 5,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !portfolioId) return;
    setLoading(true);
    getSettings(portfolioId)
      .then((r) => setForm({ ...r.settings }))
      .catch(toastApiError)
      .finally(() => setLoading(false));
  }, [open, portfolioId]);

  const save = async () => {
    setLoading(true);
    try {
      const r = await patchSettings(portfolioId, form);
      toast.success("Desk settings saved");
      onSaved?.(r.settings);
      onOpenChange(false);
    } catch (e) {
      toastApiError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="desk-settings-dialog"
        className="border border-[var(--aurion-border)] bg-[var(--aurion-panel)] text-[var(--aurion-text)]"
      >
        <DialogHeader>
          <DialogTitle className="font-heading tracking-widest">
            Desk / Auto Mode
          </DialogTitle>
          <DialogDescription className="text-[var(--aurion-muted)]">
            Server-enforced. Timer, threshold, and cap apply to every new
            proposal on this portfolio.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="flex items-center justify-between border border-[var(--aurion-border)] bg-[var(--aurion-panel-2)] px-3 py-2">
            <div>
              <div className="text-sm">Auto Mode</div>
              <div className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                When on, expired proposals are decided by NUMIROCK
              </div>
            </div>
            <Switch
              data-testid="settings-auto-mode"
              checked={!!form.auto_mode}
              onCheckedChange={(v) => setForm((f) => ({ ...f, auto_mode: !!v }))}
            />
          </div>

          <Field
            id="timer"
            label="Timer (seconds)"
            hint="15 – 86400. Countdown before auto-decision."
            testid="settings-timer"
            value={form.timer_seconds}
            onChange={(v) => setForm((f) => ({ ...f, timer_seconds: v }))}
            min={15}
            max={86400}
          />
          <Field
            id="threshold"
            label="Conviction threshold"
            hint="0 – 100. Auto-execute only if conviction ≥ threshold."
            testid="settings-threshold"
            value={form.conviction_threshold}
            onChange={(v) => setForm((f) => ({ ...f, conviction_threshold: v }))}
            min={0}
            max={100}
          />
          <Field
            id="cap"
            label="Daily auto-trade cap"
            hint="1 – 100. Auto-reject once cap hits."
            testid="settings-cap"
            value={form.daily_auto_trade_cap}
            onChange={(v) => setForm((f) => ({ ...f, daily_auto_trade_cap: v }))}
            min={1}
            max={100}
          />

          <Button
            data-testid="settings-save"
            onClick={save}
            disabled={loading}
            className="aurion-btn-gold h-10 w-full text-xs font-semibold uppercase tracking-widest"
          >
            {loading ? "Saving…" : "Save settings"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ id, label, hint, value, onChange, testid, min, max }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs uppercase tracking-widest text-[var(--aurion-muted)]">
        {label}
      </Label>
      <Input
        id={id}
        data-testid={testid}
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value || "0", 10))}
        className="mt-2 h-11 border-[var(--aurion-border)] bg-[var(--aurion-panel-2)] font-mono"
      />
      <div className="mt-1 text-[10px] text-[var(--aurion-muted)]">{hint}</div>
    </div>
  );
}
